import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { publicClient } from '@/api/publicClient';
import type { components } from '@/api/generated/schema';

export type PublicEstimate = components['schemas']['PublicEstimateResponse'];
export type PublicEstimateDetail = components['schemas']['PublicEstimateDetail'];

/**
 * A 404 from the link: malformed, revoked, unknown, or answered too long ago.
 * The API makes every one of those the same answer on purpose, so the page
 * does too — one "no longer valid" page, never a guess at which it was.
 */
export class PublicEstimateGoneError extends Error {
  constructor() {
    super('This link is no longer valid');
    this.name = 'PublicEstimateGoneError';
  }
}

/**
 * The customer's estimate, read through its link (M2-07) with no sign-in —
 * through `publicClient`, never `apiClient` (see that file for why).
 *
 * Not refetched on focus: the customer is reading a document, and the page
 * jumping under them because the office answered it in another building would
 * be worse than a stale banner. No retry on a 404 — it is an answer.
 */
export function usePublicEstimate(token: string | undefined) {
  return useQuery({
    enabled: token !== undefined,
    queryKey: ['public-estimate', token] as const,
    queryFn: async () => {
      const { data, response } = await publicClient.GET('/api/v1/public/estimates/{token}', {
        params: { path: { token: token! } },
      });
      if (response.status === 404) throw new PublicEstimateGoneError();
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/public/estimates/{token} failed: ${response.status}`);
      }
      return data;
    },
    retry: false,
    refetchOnWindowFocus: false,
  });
}

// --- The customer's answer (M2-08, pass B) -----------------------------------

export type PublicApproveRequest = components['schemas']['PublicApproveEstimateRequest'];
export type PublicDeclineRequest = components['schemas']['PublicDeclineEstimateRequest'];
export type PublicConsent = components['schemas']['PublicEstimateConsent'];

/**
 * The link cannot take an answer now (409), in the API's own sentence: it was
 * already approved or declined (by either path), it expired, or a newer
 * revision was sent. The sentence is the whole explanation, so it is carried
 * verbatim.
 */
export class PublicAnswerRefusedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PublicAnswerRefusedError';
  }
}

/**
 * A 400: the API's sentence per field (`SignerName`, `SignatureImage`, `Consented`, …).
 * Only ever built with at least one sentence (see `answerFailure`).
 */
export class PublicAnswerInvalidError extends Error {
  constructor(public readonly errors: Record<string, string[]>) {
    super(Object.values(errors).flat()[0] ?? '');
    this.name = 'PublicAnswerInvalidError';
  }
}

/**
 * A fresh id for one attempt at answering. `crypto.randomUUID` exists only in
 * a secure context — the live site is one, a phone on a LAN dev server is
 * not — so it falls back to a v4 UUID from `getRandomValues`, which exists
 * everywhere.
 */
export function newSubmissionId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * The customer approves (signing) or declines, through `publicClient`.
 *
 * ⚠️ **Idempotent by `submissionId`.** The caller keeps one id per attempt and
 * sends it again on a retry: a response lost on a slow phone, resent, is
 * answered 200 with the page as the first one left it — never a second record,
 * never a 409 against the customer's own answer.
 *
 * On success the API returns the page as it now reads, which goes straight
 * into the read's cache — the page turns Approved/Declined without another
 * request. A 409 or 404 re-reads the page instead (the office answered, a
 * revision was sent, the link was revoked); a 400 about the consent version
 * re-reads it too, so the wording the customer is shown is the server's
 * current one.
 */
export function usePublicAnswer(token: string) {
  const queryClient = useQueryClient();
  const key = ['public-estimate', token] as const;

  const onSuccess = (page: PublicEstimate) => queryClient.setQueryData(key, page);
  const onError = (error: Error) => {
    if (
      error instanceof PublicAnswerRefusedError ||
      error instanceof PublicEstimateGoneError ||
      (error instanceof PublicAnswerInvalidError && error.errors.ConsentVersion)
    ) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  };

  const approve = useMutation({
    mutationFn: async (body: PublicApproveRequest) => {
      const { data, error, response } = await publicClient.POST(
        '/api/v1/public/estimates/{token}/approve',
        { params: { path: { token } }, body },
      );
      if (!response.ok || !data) throw answerFailure(error, response, 'approve');
      return data;
    },
    onSuccess,
    onError,
  });

  const decline = useMutation({
    mutationFn: async (body: PublicDeclineRequest) => {
      const { data, error, response } = await publicClient.POST(
        '/api/v1/public/estimates/{token}/decline',
        { params: { path: { token } }, body },
      );
      if (!response.ok || !data) throw answerFailure(error, response, 'decline');
      return data;
    },
    onSuccess,
    onError,
  });

  return { approve, decline };
}

/**
 * ⚠️ The message never carries the URL: it would carry the token, and an
 * error's message is exactly what telemetry collects.
 */
function answerFailure(error: unknown, response: Response, what: string): Error {
  if (response.status === 404) return new PublicEstimateGoneError();
  if (response.status === 409) {
    return new PublicAnswerRefusedError(
      typeof error === 'string' ? error : 'This estimate cannot be answered.',
    );
  }
  if (response.status === 400) {
    // A 400 with no sentence keyed in it (a body the server could not bind)
    // is nothing the customer can correct field by field: it falls through to
    // the generic failure, beside the button, rather than an empty refusal.
    const problem = error as { errors?: Record<string, string[]> } | undefined;
    const errors = problem?.errors ?? {};
    if (Object.values(errors).some((sentences) => sentences.length > 0)) {
      return new PublicAnswerInvalidError(errors);
    }
  }
  return new Error(`The customer's ${what} failed: ${response.status}`);
}
